import { useTranslation } from 'react-i18next';
import Card from '../common/Card/Card';
import IconText from '../common/IconText/IconText';
import ChevronLink from '../common/ChevronLink/ChevronLink';
import VoteSplit from '../common/VoteSplit/VoteSplit';
import type { SessionData } from '../../api';
import { useAppContext } from '../../context/AppContext';
import { useFormatters } from '../../utils/texts'
import { DateTime } from 'luxon';
import { useCouncilPath } from '../../hooks/useCouncilPath';

export const MeetingCard = ({ meetingData, compact }: { meetingData: SessionData; compact?: boolean }) => {
    const councilPath = useCouncilPath();
    const { t, i18n } = useTranslation(['meetings', 'votings']);
    const { municipalityData } = useAppContext();

    const timeZone = municipalityData.casova_zona;
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    const dtMunicipalityFrom = DateTime.fromISO(meetingData.datum_od, { zone: timeZone });
    const dtMunicipalityTo = DateTime.fromISO(meetingData?.datum_do ?? '', { zone: timeZone });

    const formattedDateFrom = dtMunicipalityFrom.setLocale(currentLocale).toLocaleString(DateTime.DATE_FULL);
    const formattedDateTo = dtMunicipalityTo.isValid
        ? dtMunicipalityTo.setLocale(currentLocale).toLocaleString(DateTime.DATE_FULL)
        : null;

    let meetingDate = formattedDateFrom;
    if (formattedDateTo) {
        meetingDate = `${formattedDateFrom} – ${formattedDateTo}`;
    }

    const percent = formatter.percent2From100(meetingData.dochazka);

    const mutedAttendance = `(${t('x-outOf-y', {
        ns: 'translation',
        x: meetingData.ucast,
        y: meetingData.celkem
    })})`;

    const attendanceLabel = t('attendanceOfMembers');

    const isContentious = meetingData.pocet_tesnych_hlasovani > 0;
    const closeVotesTooltip = t('closeVotesTooltip');
    const closeVotesColor = isContentious ? undefined : 'var(--color-muted60)';

    if (compact) {
        return (
            <Card
                key={meetingData.id}
                className='meeting-row'
                link={councilPath('zasedani', meetingData.id)}
            >
                <div className='meeting-row-head'>
                    <span className='meeting-row-number'>{t('meetingNo', { num: meetingData.cislo_zasedani })}</span>
                    <ChevronLink chevronDirection='right' linkDisabled />
                </div>
                <div className='meeting-row-meta'>
                    <IconText icon='calendar_today' text={meetingDate} />
                    <IconText icon='group' text={percent} />
                    <IconText icon='select_check_box' text={`${meetingData.pocet_platnych_hlasovani}`} />
                    <div title={closeVotesTooltip}>
                        <IconText
                            icon='warning'
                            text={`${meetingData.pocet_tesnych_hlasovani}`}
                            className={isContentious ? undefined : 'is-muted'}
                            iconAndHighlightColor={closeVotesColor}
                        />
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card
            key={meetingData.id}
            className='card-list-item'
            link={councilPath('zasedani', meetingData.id)}
        >
            <h4>
                {t('meetingNo', { num: meetingData.cislo_zasedani })}
            </h4>
            <div className='card-list-item-content'>
                <IconText icon='calendar_today' text={meetingDate} highlightSubString={meetingDate} />
                <IconText
                    icon='group'
                    text={`${percent} ${mutedAttendance} ${attendanceLabel}`}
                    highlightSubString={percent}
                    mutedSubString={mutedAttendance}
                />
                <VoteSplit
                    accepted={meetingData.pocet_prijatych_hlasovani}
                    rejected={meetingData.pocet_zamitnutych_hlasovani}
                    title={`${meetingData.pocet_prijatych_hlasovani} ${t('approvedProposals', {
                        ns: 'votings'
                    })} · ${meetingData.pocet_zamitnutych_hlasovani} ${t('rejectedProposals', { ns: 'votings' })}`}
                />
                <div title={closeVotesTooltip}>
                    <IconText
                        icon='warning'
                        text={t('closeVotesFlag', { count: meetingData.pocet_tesnych_hlasovani })}
                        highlightSubString={isContentious ? `${meetingData.pocet_tesnych_hlasovani}` : undefined}
                        className={isContentious ? undefined : 'is-muted'}
                        iconAndHighlightColor={closeVotesColor}
                    />
                </div>
                <div className='link-container'>
                    <IconText
                        icon='select_check_box'
                        text={`${meetingData.pocet_platnych_hlasovani} ${t('votings', { ns: 'votings' }).toLowerCase()}`}
                        highlightSubString={`${meetingData.pocet_platnych_hlasovani}`}
                    />
                    <ChevronLink text={t('details', { ns: 'translation' })} chevronDirection='right' linkDisabled />
                </div>
            </div>
        </Card>
    );
};
